'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { reduxLogger } from '@/core/utils/logger';
import LoadingSystem from '@/shared/components/loading/LoadingSystem';
import '../../styles/components/loading.css';

interface GlobalLoadingContextType {
  showLoading: (message?: string) => void;
  hideLoading: () => void;
  isLoading: boolean;
}

const GlobalLoadingContext = createContext<GlobalLoadingContextType | undefined>(undefined);

export function useGlobalLoading() {
  const context = useContext(GlobalLoadingContext);
  if (!context) {
    throw new Error('useGlobalLoading must be used within GlobalLoadingProvider');
  }
  return context;
}

interface GlobalLoadingProviderProps {
  children: React.ReactNode;
}

/**
 * GlobalLoadingProvider - Centralized loading state management
 * Coordinates loading states across auth, dashboard, and data fetching
 */
export function GlobalLoadingProvider({ children }: GlobalLoadingProviderProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('Loading...');
  const loadingCountRef = React.useRef(0); // Track nested loading calls
  
  // Adaptive timeout based on the operation
  // Shorter timeouts to prevent stuck states - page navigation will reset anyway
  const getTimeoutForMessage = (msg: string): number => {
    if (msg.toLowerCase().includes('sign')) return 6000; // 6s for auth (reduced from 10s)
    if (msg.toLowerCase().includes('load')) return 10000; // 10s for data loading (reduced from 15s)
    if (msg.toLowerCase().includes('save')) return 6000; // 6s for save operations (reduced from 8s)
    return 8000; // 8s default (reduced from 12s)
  };
  
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const showLoading = useCallback((msg?: string) => {
    loadingCountRef.current++;
    reduxLogger.info('[GlobalLoadingProvider] showLoading called', {
      message: msg,
      count: loadingCountRef.current
    });
    
    setMessage(msg || 'Loading...');
    setIsLoading(true);

    // Clear any existing timer then start a new one with adaptive timeout
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const timeout = getTimeoutForMessage(msg || '');
    
    timeoutRef.current = setTimeout(() => {
      reduxLogger.warn('[GlobalLoadingProvider] Safety timeout fired', {
        message: msg,
        timeout,
        count: loadingCountRef.current
      });
      loadingCountRef.current = 0; // Reset count on timeout
      setIsLoading(false);
    }, timeout);
  }, []);

  const hideLoading = useCallback(() => {
    loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
    reduxLogger.info('[GlobalLoadingProvider] hideLoading called', {
      count: loadingCountRef.current
    });
    
    // Only hide if no more loading operations
    if (loadingCountRef.current === 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsLoading(false);
    }
  }, []);

  return (
    <GlobalLoadingContext.Provider value={{ showLoading, hideLoading, isLoading }}>
      {children}
      {/* Optional global loading overlay - rarely used now */}
      {isLoading && (
        <div className="animate-fade-in" data-testid="global-loading-overlay">
          <LoadingSystem show={true} message={message} size="large" fullScreen />
        </div>
      )}
    </GlobalLoadingContext.Provider>
  );
}
